import { initArbitrage } from '@/helpers/composables/useArbitrage';
import { applyRoute } from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { generateUUIDv4 } from '@/helpers/uuid-helper';
import { RouterMeta } from '@/routing/config';
import { handleSubdomainSwitch } from '@/routing/helpers/subdomain-helper';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';
// check for first routing and possibly update store state from url (e.g. filters from url)

export default defineNuxtRouteMiddleware(async (to, from) => {
	const firstRouting = from?.name === undefined || from?.name === 'app.refreshtoken';
	const context = useNuxtApp();

	if (import.meta.client) {
		const routingStore = useRoutingStore(context.$pinia);
		const trackingStore = useTrackingStore(context.$pinia);
		const languageStore = useLanguageStore(context.$pinia);

		// Handle route changes that switch subdomain
		if (to.name) {
			await handleSubdomainSwitch({ pinia: context.$pinia }, to);
		}

		// persist scroll position for each tab
		if (!firstRouting) {
			const tab = (
				from?.meta?.tab === 'sports' ? `${from?.meta?.tab}-${from?.meta?.data}` : from?.meta?.tab
			) as RouterMeta['tab'];

			routingStore.setScrollPosition({ tab, y: window.scrollY });
		}

		if (firstRouting) {
			initArbitrage(to, languageStore.country);
		}

		// Track page view on client only
		const pageViewUUID = generateUUIDv4();
		trackingStore.setPageViewUUID(pageViewUUID);

		const { meta, fullPath } = to;

		// ###### TRACK PAGE ######
		// We deliberately do not `await trackPage` here, to avoid blocking the router
		// on slow async work. Instead, we ensure critical prerequisites (like urlMetadata)
		// are ready before calling it.
		// Here the async actions that happen in trackPage, that are already resolved beforehand and don't yield additional awaits:
		// - urlMetadata: resolved
		// - getContexts: only unresolved if there is an unknown pagetype
		const metaContexts = (meta?.contexts || []) as string[];

		// we track page views ourselves for title detail pages and (nearly all) sport pages
		const sportPageExceptions = [
			// 'app.sports.subdomain.all', // this isn't included because it doesn't have any sport context
			'app.sports.subdomain.overview',
			'app.sports.subdomain.competition', // @todo implement with sport context
			'app.sports.subdomain.event',
			'app.sports.subdomain.team|competition',
		];

		//handle first routing subgenre in routingConfig, assign permanentAudienceSubgenre in beforeEnter() subgenre route
		const firstRoutingIsSubgenre =
			(from?.name === null || from?.name === 'app.refreshtoken') &&
			((to.name as string) || '').includes('subgenre');

		if (
			!metaContexts.includes('title') &&
			!sportPageExceptions.some(prefix => ((to.name as string) || '').startsWith(prefix)) &&
			!firstRoutingIsSubgenre
		) {
			TrackingHelper.trackPage(fullPath);
		}
	}

	// check first page view
	if ((firstRouting && import.meta.server) || (import.meta.client && to.path !== from.path)) {
		// @todo translate route parameters to filters if there are any
		await applyRoute(to, { pinia: context.$pinia });
	}
});
